import Route from '@ember/routing/route';
import { service } from '@ember/service';

export default class ScheduleIndexRoute extends Route {
  @service state;
  @service store;
  @service abilities;
  @service deskLending;
  @service currentAdmin;
  @service featureFlags;

  async model() {
    const result = {
      covid19VaccineDocumentTrackingEnabled: false,
      covid19VaccineDocumentLink: null,
      emergencyNotificationConfiguration: null,
      userHasPhoneNumber: false,
      employeeId: null,
      assignedDeskId: null,
      lendableDesks: [],
    };
    if (this.state.currentLocationEnabledDesks?.id) {
      const deskLocation = await this.store.findRecord('desk-location', this.state.currentLocationEnabledDesks.id, {
        reload: true,
        include: 'workplace-days',
      });
      const workplaceDays = this.store
        .peekAll('workplaceDay')
        .filter((day) => {
          return day.belongsTo('deskLocation').id() === this.state.currentLocationEnabledDesks.id;
        })
        .sort((a, b) => a.id - b.id);

      result.workplaceDays = workplaceDays;

      result.employeeId = this.currentAdmin.employee?.id ?? null;

      if (this.abilities.can('lend desk for desks') && deskLocation.deskLendingEnabled) {
        // Always fetch assigned desk as baseline (needed for single-desk and as fallback)
        const [desks, myLentDesk] = await Promise.all([
          this.store
            .query('desk', {
              filter: {
                'location-id': this.state.currentLocation.id,
                'assigned-to': this.state.currentUser.email,
              },
            })
            .catch(() => ({ slice: () => [] })),
          this.deskLending.fetchMyLentDesk(this.state.currentLocation.id),
        ]);

        const assignedDesk = desks.slice()[0];
        if (assignedDesk) {
          result.assignedDeskId = assignedDesk.id;
        } else if (myLentDesk?.deskId) {
          result.assignedDeskId = String(myLentDesk.deskId);
        }

        if (this.featureFlags.isEnabled('reverse-hoteling-multiple-desks')) {
          // Multi-desk: lendable desks also fetched in setupController via new endpoint
          result.deskLendingAvailable = true;
        }
      }
    }

    if (this.abilities.can('see-features emergency-notification')) {
      result.emergencyNotificationConfiguration =
        await this.state.currentLocation.getEmergencyNotificationConfiguration();

      const userPhoneNumber = !!this.state.currentUser.phoneNumber;

      if (result.emergencyNotificationConfiguration.passivePhoneNumberCollectionEnabledAt) {
        if (userPhoneNumber) {
          result.userHasPhoneNumber = true;
        } else {
          const employee = (
            await this.store.query('employee', {
              filter: {
                'email-in': this.state.currentUser.email,
              },
            })
          ).slice()[0];

          result.userHasPhoneNumber = !!employee?.phoneNumber;
        }
      }
    }

    const userDocumentTemplates = await this.store.findAll('user-document-template');
    const userDocumentTemplate = userDocumentTemplates.findBy('identifier', 'covid-19-vaccine-card');

    if (userDocumentTemplate?.id) {
      const userDocumentTemplateConfigurations = await this.store.query('user-document-template-configuration', {
        filter: {
          'user-document-template-id': userDocumentTemplate.id,
          'location-id': this.state.currentLocation.id,
        },
      });
      const userDocumentTemplateConfiguration = userDocumentTemplateConfigurations.slice()[0];
      result.covid19VaccineDocumentTrackingEnabled = !!userDocumentTemplateConfiguration?.active;

      const userDocuments = await this.store.query('user-document', {
        include: 'user-document-links',
        filter: {
          user: this.state.currentUser.id,
          'user-document-template-identifier': userDocumentTemplate.identifier,
        },
      });
      result.covid19VaccineDocumentLink = userDocuments.slice()[0]?.userDocumentLinks.content[0];
    }

    return result;
  }

  setupController(controller) {
    super.setupController(...arguments);

    const { scheduleEligibilityOffset } = controller.state.currentLocation;
    const offset = controller.storedScheduleEligibilityOffset;

    if (offset !== scheduleEligibilityOffset) {
      if (scheduleEligibilityOffset == null) {
        controller.closeScheduleEligibilityOffsetNotification();
      } else {
        controller.removeStoredScheduleEligibilityOffset();
      }
    }

    controller.registrations.dumpRecords();
    controller.registrations.loadMoreTask.perform(controller.daysToLoad);

    if (this.featureFlags.isEnabled('reverse-hoteling-multiple-desks')) {
      controller.fetchLendableDesks();
    } else {
      controller.fetchLendingStatus();
    }
  }

  resetController(controller, isExiting) {
    if (isExiting) {
      controller.registrations.dumpRecords();
    }
  }
}
